import { useState, useEffect, useCallback } from "react";
import { Star, User, Calendar, Stethoscope } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Modal } from "../components/ui/modal";
import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

export const fetchReviews = async (token, veterinaryId) => {
  const response = await axios.get(`${apiUrl}/api/v1/veterinaries/reviews`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { veterinary_id: veterinaryId },
  });
  
  return response.data.reviews;
};

const ReviewsScreen = () => {
  const { token, user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const loadReviews = useCallback(async () => {
    if (!token || !user?.veterinary?.id) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await fetchReviews(token, user.veterinary.id);
      setReviews(data);
    } catch (err) {
      console.error("Failed to load reviews:", err);
      setError("Failed to fetch reviews. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleRowClick = (review) => {
    setSelectedReview(review);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedReview(null);
  };

  const renderStars = (rating) => {
    return (
      <div style={styles.starsContainer}>
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            style={{
              ...styles.star,
              fill: star <= rating ? "#F59E0B" : "none",
              color: star <= rating ? "#F59E0B" : "#D1D5DB",
            }}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (!token) {
    return (
      <div style={styles.container}>
        <div style={styles.errorMessage}>Please log in to view reviews.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingMessage}>Loading reviews...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>Reviews</h1>
      </div>

      {error && (
        <div style={styles.error}>
          {error}
          <button 
            onClick={loadReviews} 
            style={styles.retryButton}
          >
            Retry
          </button>
        </div>
      )}

      <div style={styles.reviewsSection}>
        <h2 style={styles.sectionTitle}>Customer Reviews</h2>
        
        {reviews.length === 0 ? (
          <div style={styles.emptyMessage}>
            No reviews found.
          </div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.tableHeaderCell}>Customer</th>
                  <th style={styles.tableHeaderCell}>Pet & Service</th>
                  <th style={styles.tableHeaderCell}>Rating</th>
                  <th style={styles.tableHeaderCell}>Comment</th>
                  <th style={styles.tableHeaderCell}>Date</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((review) => (
                  <tr
                    key={review.id}
                    style={styles.tableRow}
                    onClick={() => handleRowClick(review)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#F9FAFB";
                      e.currentTarget.style.cursor = "pointer";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <td style={styles.tableCell}>
                      <div style={styles.userCell}>
                        <div style={styles.userIcon}>
                          <User style={styles.userCellIcon} />
                        </div>
                        <span style={styles.userName}>
                          {review.user?.first_name || 'Unknown'} {review.user?.last_name || ''}
                        </span>
                      </div>
                    </td>
                    <td style={styles.tableCell}>
                      <div>
                        <div style={styles.petName}>
                          {review.appointment?.pet_name || 'N/A'}
                        </div>
                        <div style={styles.serviceName}>
                          {review.appointment?.service_name || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td style={styles.tableCell}>
                      {renderStars(review.rating)}
                    </td>
                    <td style={{ ...styles.tableCell, ...styles.commentCell }}>
                      {review.comment && review.comment.length > 60
                        ? `${review.comment.substring(0, 60)}...`
                        : review.comment || 'No comment'}
                    </td>
                    <td style={{ ...styles.tableCell, color: "#6b7280" }}>
                      {review.created_at ? formatDate(review.created_at) : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title="Review Details">
        {selectedReview && (
          <div style={styles.modalContent}>
            <div style={styles.detailSection}>
              <div style={styles.detailHeader}>
                <User style={styles.detailIcon} />
                <span style={styles.detailLabel}>Customer</span>
              </div>
              <div style={styles.detailValue}>
                {selectedReview.user?.first_name || 'Unknown'} {selectedReview.user?.last_name || ''}
              </div>
            </div>

            <div style={styles.detailSection}>
              <div style={styles.detailHeader}>
                <Stethoscope style={styles.detailIcon} />
                <span style={styles.detailLabel}>Pet & Service</span>
              </div>
              <div style={styles.detailValue}>
                {selectedReview.appointment?.pet_name || 'N/A'}
              </div>
              <div style={styles.detailSubValue}>
                {selectedReview.appointment?.service_name || 'N/A'}
              </div>
            </div>

            <div style={styles.detailSection}>
              <div style={styles.detailHeader}>
                <Star style={styles.detailIcon} />
                <span style={styles.detailLabel}>Rating</span>
              </div>
              <div style={styles.ratingContainer}>
                {renderStars(selectedReview.rating)}
                <span style={styles.ratingText}>
                  {selectedReview.rating || 0} out of 5
                </span>
              </div>
            </div>

            <div style={styles.detailSection}>
              <div style={styles.detailHeader}>
                <Calendar style={styles.detailIcon} />
                <span style={styles.detailLabel}>Date</span>
              </div>
              <div style={styles.detailValue}>
                {selectedReview.created_at ? formatDate(selectedReview.created_at) : 'N/A'}
              </div>
            </div>

            <div style={styles.detailSection}>
              <div style={styles.detailLabel}>Comment</div>
              <div style={styles.commentBox}>
                {selectedReview.comment || 'No comment provided'}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ReviewsScreen;

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#ffffff",
    padding: "24px",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: "24px",
    borderBottom: "1px solid #e5e7eb",
  },
  headerTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#111827",
  },
  error: {
    color: "#EF4444",
    fontSize: "14px",
    marginTop: "16px",
    padding: "12px",
    backgroundColor: "#FEE2E2",
    borderRadius: "4px",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
  },
  retryButton: {
    padding: "4px 12px",
    backgroundColor: "#DC2626",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
  },
  errorMessage: {
    color: "#EF4444",
    fontSize: "14px",
    marginTop: "16px",
    padding: "8px",
    backgroundColor: "#FEE2E2",
    borderRadius: "4px",
    textAlign: "center",
  },
  loadingMessage: {
    textAlign: "center",
    padding: "40px",
    color: "#6B7280",
    fontSize: "16px",
  },
  emptyMessage: {
    textAlign: "center",
    padding: "40px",
    color: "#6B7280",
    fontSize: "16px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    backgroundColor: "#F9FAFB",
  },
  reviewsSection: {
    marginTop: "24px",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#111827",
    marginBottom: "16px",
  },
  tableContainer: {
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    overflow: "visible",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e5e7eb",
  },
  tableHeaderCell: {
    padding: "12px 16px",
    color: "#6b7280",
    fontWeight: "500",
    fontSize: "14px",
    textAlign: "left",
    borderBottom: "1px solid #e5e7eb",
  },
  tableRow: {
    borderBottom: "1px solid #f3f4f6",
    transition: "background-color 150ms",
  },
  tableCell: {
    padding: "16px",
    verticalAlign: "middle",
  },
  userCell: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  userIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#FFF7ED",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  userCellIcon: {
    width: "20px",
    height: "20px",
    color: "#F97316",
  },
  userName: {
    fontWeight: "500",
    color: "#111827",
  },
  petName: {
    fontWeight: "500",
    color: "#111827",
    marginBottom: "4px",
  },
  serviceName: {
    fontSize: "14px",
    color: "#6b7280",
  },
  starsContainer: {
    display: "flex",
    gap: "4px",
  },
  star: {
    width: "16px",
    height: "16px",
  },
  commentCell: {
    color: "#4b5563",
    maxWidth: "300px",
  },
  modal: {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: "16px",
    },
    container: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      maxWidth: "600px",
      width: "100%",
      maxHeight: "90vh",
      overflow: "auto",
      boxShadow:
        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    },
    header: {
      padding: "20px 24px",
      borderBottom: "1px solid #E5E7EB",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    title: {
      fontSize: "18px",
      fontWeight: "600",
      color: "#111827",
    },
    closeButton: {
      padding: "4px",
      border: "none",
      backgroundColor: "transparent",
      cursor: "pointer",
      borderRadius: "4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#6B7280",
    },
    closeIcon: {
      width: "20px",
      height: "20px",
    },
    content: {
      padding: "24px",
    },
  },
  modalContent: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  detailSection: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  detailHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  detailIcon: {
    width: "18px",
    height: "18px",
    color: "#F97316",
  },
  detailLabel: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  detailValue: {
    fontSize: "16px",
    fontWeight: "500",
    color: "#111827",
  },
  detailSubValue: {
    fontSize: "14px",
    color: "#6B7280",
  },
  ratingContainer: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  ratingText: {
    fontSize: "14px",
    color: "#6B7280",
  },
  commentBox: {
    padding: "16px",
    backgroundColor: "#F9FAFB",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#374151",
    lineHeight: "1.6",
    border: "1px solid #E5E7EB",
  },
};